import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import { getChartModeTypes, getDefaultChartMode } from '@aytm/app/stats/page/question-views/mn/lib/chart/chartModes'
import { getProductsAnswer } from '@aytm/app/stats/page/question-views/mn/components/products-section/chart/lib/answer'
import { getTraitValues } from '@aytm/app/stats/page/question-views/mn/lib/chart/traitValues'
import { getYAxisTitle, getYAxisLimits } from '@aytm/app/stats/page/question-views/mn/lib/chart/yAxis'
import {
  PRODUCT_PERCENT_CAPABLE_VIEWS,
  PRODUCT_VIEW_OPTIONS
} from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'

const ProductsChart = ({
  questionId,
  questionKey,
  qType,
  viewMode,
  answerVariants,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  traitName,
  trait,
  priceTemplate,
  // root-format flags, derived coffee-side — the legacy chart model never received
  // the format object itself, so React only sees the pre-computed flags
  ignoreTraits = false,
  ignorePercents: ignoresPercents = false,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  onChartReady
}) => {
  // live-axis bridge for the block's getDisplayRange (D11) — the products chart drives the range
  const chartRef = useChartAxisApi(onChartReady)

  // the N/A menu item is dropped from per-item charts
  const menuItems = useMemo(() => (answerVariants || []).filter(av => !av.na), [answerVariants])
  const itemsCount = menuItems.length

  const chartModeTypes = useMemo(
    () => getChartModeTypes({ viewMode, percentCapableViews: PRODUCT_PERCENT_CAPABLE_VIEWS, itemsCount }),
    [viewMode, itemsCount]
  )

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        globalChartMode,
        viewMode,
        percentCapableViews: PRODUCT_PERCENT_CAPABLE_VIEWS,
        itemsCount
      }),
    [chartModeProp, globalChartMode, viewMode, itemsCount]
  )

  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  const hcAnswers = useMemo(
    () => getProductsAnswer({ answer, answerVariants, viewMode, priceTemplate, trait, traitName }),
    [answer, answerVariants, viewMode, priceTemplate, trait, traitName]
  )

  const yAxisTitle = useMemo(
    () =>
      getYAxisTitle({
        viewMode,
        chartMode,
        options: PRODUCT_VIEW_OPTIONS,
        percentCapableViews: PRODUCT_PERCENT_CAPABLE_VIEWS
      }),
    [viewMode, chartMode]
  )
  // Split by trait plots one bar per trait value; limits taken from the ungrouped amounts would
  // clip the series actually drawn, so the axis is left to fit itself.
  const yAxisLimits = useMemo(
    () =>
      trait
        ? false
        : getYAxisLimits({ viewMode, items: menuItems, hcAnswers, skipViewModes: PRODUCT_PERCENT_CAPABLE_VIEWS }),
    [viewMode, menuItems, hcAnswers, trait]
  )

  // selector highlights the explicit per-question choice; unset/implicit → Default
  // (mirrors legacy renderChartModes — NOT the resolved mode ec/mx show)
  const selectorChartModeValue =
    chartModeProp === 'off' || chartModeTypes.includes(chartModeProp) ? chartModeProp : 'off'

  // a view with no shares draws none, whatever the question shows
  const ignorePercents = ignoresPercents || !PRODUCT_PERCENT_CAPABLE_VIEWS.includes(viewMode)

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      chartMode={chartMode}
      selectorChartModeValue={selectorChartModeValue}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={features?.view_options?.disabled}
      onChartModeChange={onChartModeChange}
      answerVariants={menuItems}
      answer={hcAnswers}
      sortOrder={sortOrder}
      yAxisTitle={yAxisTitle}
      yAxisLimits={yAxisLimits}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType
      onZoomChange={onZoomChange}
      traitName={traitName}
      trait={trait}
      traitValues={traitValues}
      ignoreTraits={ignoreTraits}
      ignorePercents={ignorePercents}
      viewMode={viewMode}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
    />
  )
}

ProductsChart.propTypes = {
  questionId: PropTypes.string,
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(PRODUCT_VIEW_OPTIONS.map(o => o.value)).isRequired,
  answerVariants: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  traitName: PropTypes.string,
  trait: PropTypes.object,
  priceTemplate: PropTypes.string,
  ignoreTraits: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

ProductsChart.displayName = 'ProductsChart'

export default ProductsChart
